import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import {
  Bookmark, CheckCircle2, Clock, Play, RotateCcw, Terminal, XCircle,
} from "lucide-react";
import {
  AppButton, Callout, Checkbox, FormField, NativeSelect, Textarea, cn,
} from "../ui";
import RichText from "../Common/RichText";
import ExamDialog from "./ExamDialog";
import { getMonacoId } from "../../utils/judgeLanguages";

// Loaded only when a paper has a coding problem — no other sitting pays for it.
const Editor = lazy(() => import("@monaco-editor/react"));

// If the editor has not come up by then (a blocked CDN, a slow lab network),
// the candidate gets a plain text box instead of a spinner for the whole exam.
const EDITOR_TIMEOUT_MS = 12000;

const VERDICT = {
  passed: { text: "Passed", tone: "tw-text-success-fg", Icon: CheckCircle2 },
  wrong: { text: "Wrong output", tone: "tw-text-danger-fg", Icon: XCircle },
  timeout: { text: "Time limit exceeded", tone: "tw-text-warning-fg", Icon: Clock },
  compile: { text: "Did not compile", tone: "tw-text-danger-fg", Icon: XCircle },
  error: { text: "Runtime error", tone: "tw-text-danger-fg", Icon: XCircle },
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** A block of program text — input, expected output, what it printed. */
function CodeBlock({ label, children }) {
  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
      <span className="tw-text-xs tw-font-medium tw-text-content-muted">{label}</span>
      {/* Focusable so a keyboard user can scroll a long line sideways. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <pre tabIndex={0} className="tw-m-0 tw-max-h-48 tw-overflow-auto tw-rounded-sm tw-border tw-border-solid tw-border-code-border tw-bg-code tw-p-2.5 tw-font-mono tw-text-xs tw-text-code-fg tw-focus-ring">
        {children === "" ? <span className="tw-text-code-fg-muted">(empty)</span> : children}
      </pre>
    </div>
  );
}

/**
 * CodingQuestion — a programming problem on the exam paper: the statement and
 * its sample tests beside a code editor, with "Run code".
 *
 * The answer is the code. It is saved as the candidate types (the runner's
 * autosave queue), in the language they chose. Code that is still exactly the
 * starter code is saved as no answer, so an untouched problem stays
 * "not answered" in the palette.
 *
 * "Run code" runs the SAMPLE tests on the server and shows each verdict; it
 * scores nothing. The hidden tests are run once, when the exam is submitted —
 * the candidate is told how many there are, never what they are.
 *
 * A draft is kept per language while the question is open, so switching from
 * Python to Java and back does not lose the Python.
 */
export default function CodingQuestion({
  question, answer, index, total, marks, onAnswer, onFlag, fontScale = 1,
  runCode, blockPaste = false, onPasteBlocked, selectable = true,
}) {
  const coding = question.coding || {};
  const languages = useMemo(() => coding.languages || [], [coding.languages]);
  const starter = coding.starterCode || {};
  const samples = coding.samples || [];

  const initialLang = (() => {
    const saved = languages.find((l) => l.name === answer?.codeLanguage);
    if (saved) return saved.name;
    const withStarter = languages.find((l) => starter[l.name]);
    return (withStarter || languages[0])?.name || "";
  })();

  const [lang, setLang] = useState(initialLang);
  const [drafts, setDrafts] = useState(() => ({
    [initialLang]: answer?.code ? answer.code : (starter[initialLang] || ""),
  }));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runError, setRunError] = useState("");
  const [custom, setCustom] = useState(false);
  const [stdin, setStdin] = useState(samples[0]?.input || "");
  const [editorReady, setEditorReady] = useState(false);
  const [editorFailed, setEditorFailed] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const editorBoxRef = useRef(null);

  const code = drafts[lang] ?? (starter[lang] || "");
  const flagged = !!answer?.isFlagged;
  const difficulty = { easy: "Easy", medium: "Medium", hard: "Hard" }[question.difficulty];

  useEffect(() => {
    if (editorReady) return undefined;
    const t = setTimeout(() => setEditorFailed(true), EDITOR_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [editorReady]);

  // "Block copy and paste" must reach the editor too. Monaco reads a paste on
  // its own hidden textarea before the page-wide guard sees it, so it is stopped
  // here, in the capture phase, before the editor gets it.
  useEffect(() => {
    const box = editorBoxRef.current;
    if (!box || !blockPaste) return undefined;
    const stop = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      onPasteBlocked?.();
    };
    box.addEventListener("paste", stop, true);
    box.addEventListener("drop", stop, true);
    return () => {
      box.removeEventListener("paste", stop, true);
      box.removeEventListener("drop", stop, true);
    };
  }, [blockPaste, onPasteBlocked, editorReady, editorFailed]);

  const save = (nextCode, nextLang) => {
    const untouched = nextCode === (starter[nextLang] || "");
    onAnswer({ code: untouched ? "" : nextCode, codeLanguage: nextLang });
  };

  const changeCode = (value) => {
    const v = value ?? "";
    setDrafts((d) => ({ ...d, [lang]: v }));
    save(v, lang);
  };

  const changeLang = (next) => {
    if (next === lang) return;
    const nextCode = drafts[next] ?? (starter[next] || "");
    setDrafts((d) => ({ ...d, [next]: nextCode }));
    setLang(next);
    setResult(null);
    setRunError("");
    save(nextCode, next);
  };

  const reset = () => {
    const fresh = starter[lang] || "";
    setDrafts((d) => ({ ...d, [lang]: fresh }));
    save(fresh, lang);
    setResult(null);
    setConfirmReset(false);
  };

  const run = async () => {
    if (!runCode || running) return;
    setRunning(true);
    setRunError("");
    setResult(null);
    try {
      const res = await runCode({ language: lang, code, stdin: custom ? stdin : undefined, custom });
      setResult(res);
    } catch (err) {
      setRunError(err?.response?.data?.message || "Your code could not be run. It is still saved — try again in a moment.");
    } finally {
      setRunning(false);
    }
  };

  const languageLabel = languages.map((l) => l.name).join(", ");

  return (
    <section aria-label={`Question ${index + 1} of ${total}, coding problem`} className="tw-flex tw-flex-col tw-gap-5">
      <header className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-x-4 tw-gap-y-2">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
          <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
            Question {index + 1}
            <span className="tw-font-normal tw-text-content-muted"> of {total} · Coding problem</span>
          </p>
          <p className="tw-m-0 tw-flex tw-flex-wrap tw-gap-x-2 tw-text-xs tw-text-content-muted">
            <span>Up to {plural(marks, "mark")}, by tests passed</span>
            {difficulty && <span>· {difficulty}</span>}
          </p>
        </div>
        <AppButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onFlag(!flagged)}
          aria-pressed={flagged}
          className={cn(flagged && "tw-text-warning-fg")}
        >
          <Bookmark size={15} aria-hidden="true" fill={flagged ? "currentColor" : "none"} />
          {flagged ? "Marked for review" : "Mark for review"}
        </AppButton>
      </header>

      <div className="tw-grid tw-items-start tw-gap-5 min-[1200px]:tw-grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        {/* ── The problem ── */}
        <div className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-4", !selectable && "tw-select-none")}>
          <h2 className="tw-m-0 tw-font-semibold tw-leading-snug tw-text-content" style={{ fontSize: `${1.125 * fontScale}rem` }}>
            {question.questionText}
          </h2>
          <div style={{ fontSize: `${0.9375 * fontScale}rem` }}>
            <RichText value={coding.statement} compact />
          </div>

          {samples.length > 0 && (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Sample tests</h3>
              {samples.map((s, i) => (
                <div key={i} className="tw-grid tw-gap-2 min-[768px]:tw-grid-cols-2">
                  <CodeBlock label={`Sample ${i + 1} — input`}>{s.input}</CodeBlock>
                  <CodeBlock label="Expected output">{s.expectedOutput}</CodeBlock>
                </div>
              ))}
            </div>
          )}
          <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-content-muted">
            Read from standard input and print to standard output.
            {coding.hiddenCount > 0
              ? ` When you submit, your code is also checked against ${plural(coding.hiddenCount, "hidden test")}. Marks follow the tests it passes.`
              : " Marks follow the tests your code passes."}
          </p>
        </div>

        {/* ── The editor ── */}
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
            <div className="tw-w-44">
              <FormField label="Language">
                <NativeSelect size="sm" value={lang} onChange={(e) => changeLang(e.target.value)}>
                  {languages.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
                </NativeSelect>
              </FormField>
            </div>
            <AppButton type="button" size="sm" variant="ghost" onClick={() => setConfirmReset(true)}>
              <RotateCcw size={15} aria-hidden="true" /> Reset code
            </AppButton>
            <span className="tw-ml-auto tw-text-xs tw-text-content-subtle">{languageLabel && `Allowed: ${languageLabel}`}</span>
          </div>

          <div
            ref={editorBoxRef}
            className={cn(!(editorFailed && !editorReady) && "tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-code-border tw-bg-code")}
          >
            {editorFailed && !editorReady ? (
              <Textarea
                aria-label={`Code, ${lang}`}
                rows={18}
                spellCheck={false}
                value={code}
                onChange={(e) => changeCode(e.target.value)}
                className="tw-font-mono"
              />
            ) : (
              <Suspense fallback={<div className="tw-h-[26rem] tw-animate-pulse tw-bg-code" aria-label="Loading the code editor" />}>
                <Editor
                  height="26rem"
                  language={getMonacoId(lang)}
                  value={code}
                  onChange={changeCode}
                  theme="vs-dark"
                  onMount={() => setEditorReady(true)}
                  options={{
                    fontSize: Math.round(14 * fontScale),
                    minimap: { enabled: false },
                    wordWrap: "on",
                    scrollBeyondLastLine: false,
                    padding: { top: 12 },
                    contextmenu: false,
                    tabSize: 4,
                    automaticLayout: true,
                    renderLineHighlight: "line",
                  }}
                />
              </Suspense>
            )}
          </div>
          {editorFailed && !editorReady && (
            <Callout tone="info">The full code editor could not load on this network, so this is a plain one. Your code is saved the same way.</Callout>
          )}

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <AppButton type="button" onClick={run} disabled={running || !runCode || !code.trim()}>
              <Play size={15} aria-hidden="true" />
              {running ? "Running…" : custom ? "Run with my input" : "Run code"}
            </AppButton>
            <Checkbox
              checked={custom}
              onChange={(e) => { setCustom(e.target.checked); setResult(null); }}
              label="Use my own input"
              size="sm"
            />
          </div>
          {custom && (
            <FormField label="Your input" hint="Exactly what your program reads from standard input.">
              <Textarea
                rows={4}
                spellCheck={false}
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
                className="tw-font-mono"
              />
            </FormField>
          )}

          <div role="status" aria-live="polite" className="tw-flex tw-flex-col tw-gap-3">
            {runError && <Callout tone="danger">{runError}</Callout>}
            {result?.custom && (
              <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3">
                <p className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                  <Terminal size={15} aria-hidden="true" /> {result.custom.status || "Finished"}
                  {result.custom.timeMs ? <span className="tw-font-normal tw-text-content-muted">· {result.custom.timeMs} ms</span> : null}
                </p>
                <CodeBlock label="Output">{result.custom.output}</CodeBlock>
                {result.custom.error && <CodeBlock label="Errors">{result.custom.error}</CodeBlock>}
              </div>
            )}
            {result?.samples && (
              <div className="tw-flex tw-flex-col tw-gap-2">
                <p className="tw-m-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                  {result.total
                    ? `${result.passed} of ${plural(result.total, "sample test")} passed`
                    : "This problem has no sample tests — try it with your own input."}
                </p>
                {result.compileError && <CodeBlock label="Compiler says">{result.compileError}</CodeBlock>}
                {!result.compileError && result.samples.map((r) => {
                  const v = VERDICT[r.verdict] || VERDICT.error;
                  return (
                    <details key={r.index} className="tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-2" open={r.verdict !== "passed"}>
                      <summary className={cn("tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-medium", v.tone)}>
                        <v.Icon size={15} aria-hidden="true" /> Sample {r.index}: {v.text}
                        {r.timeMs ? <span className="tw-font-normal tw-text-content-muted">· {r.timeMs} ms</span> : null}
                      </summary>
                      <div className="tw-mt-2 tw-grid tw-gap-2 min-[768px]:tw-grid-cols-2">
                        <CodeBlock label="Expected">{r.expectedOutput}</CodeBlock>
                        <CodeBlock label="Your output">{r.output}</CodeBlock>
                        {r.error && <div className="min-[768px]:tw-col-span-2"><CodeBlock label="Errors">{r.error}</CodeBlock></div>}
                      </div>
                    </details>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <ExamDialog
        open={confirmReset}
        size="sm"
        title="Reset your code?"
        description={`Your ${lang} code goes back to the starting code. This cannot be undone.`}
        onClose={() => setConfirmReset(false)}
        footer={(
          <>
            <AppButton type="button" variant="outline" onClick={() => setConfirmReset(false)}>Keep my code</AppButton>
            <AppButton type="button" variant="destructive" onClick={reset}>Reset</AppButton>
          </>
        )}
      >
        <span className="tw-sr-only">Confirm reset</span>
      </ExamDialog>
    </section>
  );
}
